import { useQuery } from '@tanstack/react-query';
import { PlusIcon } from 'lucide-react';
import { useId, useMemo, useState } from 'react';

import { ErrorState } from '@/components/shell/ErrorState';
import { DataTable, type TableColumn, type TableRow } from '@/components/table/DataTable';
import { BadgeCell, TextCell } from '@/components/table/cells';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/lib/auth/auth-context';
import { formatDateTime } from '@/lib/format';

import { PromotionFormDialog } from './PromotionFormDialog';
import { formatRevenue, PLAN_LABELS, STATE_TONES } from './promotion-labels';
import { promotionsQueryOptions } from './promotions-query';
import type { PromotionsSearch } from './promotions-search';

const COLUMNS: readonly TableColumn[] = [
  { key: 'code', label: 'Code', width: '1.4fr' },
  { key: 'discount', label: 'Discount', width: '1.3fr' },
  { key: 'state', label: 'State', width: '0.8fr' },
  { key: 'claims', label: 'Claims', width: '0.6fr', align: 'right' },
  { key: 'purchases', label: 'Purchases', width: '0.9fr', align: 'right' },
  { key: 'active', label: 'Still subscribed', width: '0.8fr', align: 'right' },
  { key: 'revenue', label: 'Revenue', width: '1.1fr', align: 'right' },
  { key: 'ends', label: 'Ends', width: '0.9fr' },
];

export interface PromotionsScreenProps {
  readonly search: PromotionsSearch;
  readonly onSearchChange: (next: PromotionsSearch) => void;
  readonly onOpenCampaign: (id: string) => void;
}

/**
 * Premium coupon campaigns and what each has brought in. The numbers are
 * claims typed in the app plus the purchases RevenueCat attributed to the
 * campaign's store offers; the prices themselves live in the stores.
 */
export function PromotionsScreen({ search, onSearchChange, onOpenCampaign }: PromotionsScreenProps) {
  const { api, can } = useAuth();
  const archivedId = useId();
  const [creating, setCreating] = useState(false);
  const includeArchived = search.archived === true;

  const campaigns = useQuery(promotionsQueryOptions(api, includeArchived));

  const rows: readonly TableRow[] = useMemo(
    () =>
      (campaigns.data ?? []).map((campaign) => ({
        id: campaign.id,
        onOpen: () => {
          onOpenCampaign(campaign.id);
        },
        cells: [
          <TextCell key="code" value={campaign.code} sub={campaign.name} mono weight="medium" />,
          <TextCell key="discount" value={campaign.discountLabel} sub={PLAN_LABELS[campaign.appliesTo]} />,
          <BadgeCell key="state" label={campaign.state} tone={STATE_TONES[campaign.state]} dot />,
          <TextCell key="claims" value={String(campaign.stats.claims)} align="right" mono />,
          <TextCell
            key="purchases"
            value={String(campaign.stats.purchases)}
            sub={`iOS ${String(campaign.stats.purchasesIos)} / Android ${String(campaign.stats.purchasesAndroid)}`}
            align="right"
            mono
          />,
          <TextCell key="active" value={String(campaign.stats.activeSubscribers)} align="right" mono />,
          <TextCell key="revenue" value={formatRevenue(campaign.stats.revenue)} align="right" mono />,
          <TextCell
            key="ends"
            value={campaign.endsAt === null ? 'No end date' : formatDateTime(campaign.endsAt)}
            {...(campaign.endsAt === null ? { tone: 'muted' as const } : {})}
          />,
        ],
      })),
    [campaigns.data, onOpenCampaign],
  );

  if (campaigns.isError) {
    return (
      <ErrorState
        title="Could not load promotions"
        message={campaigns.error.message}
        onRetry={() => {
          void campaigns.refetch();
        }}
      />
    );
  }

  const newButton = can.mutate ? (
    <Button
      type="button"
      size="sm"
      onClick={() => {
        setCreating(true);
      }}
    >
      <PlusIcon aria-hidden="true" className="size-3.5" />
      New campaign
    </Button>
  ) : null;

  return (
    <div className="flex max-w-[1200px] flex-col gap-3">
      <DataTable
        caption="Promotions"
        title="Premium campaigns"
        count={
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-2">
              <Switch
                id={archivedId}
                checked={includeArchived}
                onCheckedChange={(checked) => {
                  onSearchChange({ ...search, archived: checked ? true : undefined });
                }}
              />
              <Label htmlFor={archivedId} className="text-[13px] text-muted-foreground">
                Show archived
              </Label>
            </span>
            {newButton}
          </span>
        }
        columns={COLUMNS}
        rows={rows}
        isLoading={campaigns.isPending}
        emptyTitle="No campaigns"
        emptyBody={
          includeArchived
            ? 'No campaign has been registered yet.'
            : 'No campaign is running or scheduled. Archived ones are behind the switch above.'
        }
        {...(newButton === null ? {} : { emptyAction: newButton })}
      />

      {creating ? (
        <PromotionFormDialog
          campaign={null}
          onClose={() => {
            setCreating(false);
          }}
          onSaved={(campaign) => {
            onOpenCampaign(campaign.id);
          }}
        />
      ) : null}
    </div>
  );
}
